"use client"

import type React from "react"

import { useState } from "react"
import { motion } from "framer-motion"
import { Mail, Phone, Users, CheckCircle, AlertCircle, Send, X } from "lucide-react"
import ImagesDev from "../../api/images"
import Link from "../../components/Link"

interface FormData {
  email_address: string
  phone_number: string
  volunteer: string
  first_name: string
  surname: string
  vip: string
  isEmailAvailable: boolean
  isPhoneValid: boolean
}

interface FormErrors {
  email_address?: string
  phone_number?: string
  volunteer?: string
  first_name?: string
  surname?: string
  vip?: string
  isEmailAvailable?: boolean
  isPhoneValid?: boolean
}

export default function VolunteerPage() {
  // const [formData_, setFormData_] = useState<FormData>({
  //   email_address: "",
  //   phone_number: "",
  //   volunteer: "",
  // })
  const [formData, setFormData] = useState<FormData>({
    first_name: "",
    surname: "",
    email_address: "",
    phone_number: "",
    volunteer: "",
    vip: "false",
    isEmailAvailable: false,
    isPhoneValid: false,
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false)

  const roles = [
    { id: "protocol", label: "Protocol", description: "Manage event procedures and coordination" },
    { id: "hospitality", label: "Hospitality", description: "Assist guests with comfort, refreshments, and directions" },
    { id: "front-desk", label: "Front Desk", description: "Welcome guests and handle registration" },
    { id: "usher", label: "Usher", description: "Guide attendees and maintain order" },
    { id: "media", label: "Media", description: "Handle photography and social media" },
  ]

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePhone_number = (number: string): boolean => {
    // Remove all non-digit characters
    const cleanNumber = number.replace(/\D/g, "")

    // WhatsApp numbers should be between 10-15 digits
    // Most international numbers are 10-14 digits, but allowing up to 15 for flexibility
    if (cleanNumber.length < 10 || cleanNumber.length > 15) {
      return false
    }

    // Check if it starts with a country code (1-4 digits)
    // Most common patterns: +1, +44, +91, +234, etc.
    const hasValidCountryCode = /^(\+?[1-9]\d{0,3})?[1-9]\d{8,13}$/.test(number.replace(/[\s\-$$$$]/g, ""))

    return hasValidCountryCode
  }

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.email_address) {
      newErrors.email_address = "Email is required"
    } else if (!validateEmail(formData.email_address)) {
      newErrors.email_address = "Please enter a valid email address"
    }

    if (!formData.phone_number) {
      newErrors.phone_number = "WhatsApp number is required"
    } else if (!validatePhone_number(formData.phone_number)) {
      newErrors.phone_number = "Please enter a valid WhatsApp number with country code"
    }

    if (!formData.volunteer) {
      newErrors.volunteer = "Please select a volunteer role"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault()

  //   if (!validateForm()) return

  //   setIsSubmitting(true)

  //   // Simulate API call
  //   await new Promise((resolve) => setTimeout(resolve, 2000))

  //   setIsSubmitting(false)
  //   setIsSubmitted(true)

  //   // Reset form after success
  //   setTimeout(() => {
  //     // setFormData({ email_address: "", phone_number: "", volunteer: "", vip: "",  })
  //     // setIsSubmitted(false)
  //   }, 3000)
  // }

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!validateForm()) return;

  setIsSubmitting(true);

  try {
    // Step 1: Check if user exists
    const res = await fetch(
      `https://tsoministries.com/wp-json/custom-form/v1/thp-by-email?email=${encodeURIComponent(formData.email_address)}&t=${Date.now()}`
    );

    if (!res.ok) {
      const errorText = await res.text();
      console.error("Failed to check user existence:", res.status, errorText);
      // alert("We couldn't verify your email. Please try again.");
      setShowRegisterPrompt(true);
      return;
    }

    const data = await res.json();

    if (data?.email_address) {
      // User found — Update
      const updatePayload = {
        phone_number: formData.phone_number,
        volunteer: formData.volunteer,
      };

      const updateRes = await fetch(
        `https://tsoministries.com/wp-json/custom-form/v1/thp-update/${data.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        }
      );

      if (!updateRes.ok) {
        const errorBody = await updateRes.text();
        console.error("Update failed:", updateRes.status, errorBody);
        alert("Something went wrong while updating your information. Please try again later.");
        return;
      }

      // Success
      setUserId(data.id);
      setIsSubmitted(true);
    } else {
      // User not found — Show prompt
      setShowRegisterPrompt(true);
    }
  } catch (err) {
    console.error("Unexpected error during submission:", err);
    alert("An unexpected error occurred. Please check your internet connection or try again later.");
  } finally {
    setIsSubmitting(false);
  }
};

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-indigo-100 flex items-center justify-center p-4 relative">
        <div className="absolute h-[100%] inset-0 bg-black/80">
          <img
            src={"https://tsoministries.com/wp-content/uploads/2025/07/thp_update.jpeg"}
            alt="Background"
            className="fixed inset-0 w-full h-full object-cover opacity-50 blur-2xl"
          />
        </div>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full relative"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          </motion.div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-600">
            Your volunteer application has been submitted successfully. We'll contact you soon!
          </p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-indigo-100 py-12 px-4 relative">
      <div className="absolute h-[100%] inset-0 bg-black/80">
        <img
          src={"https://tsoministries.com/wp-content/uploads/2025/07/thp_update.jpeg"}
          alt="Background"
          className="fixed inset-0 w-full h-full object-cover opacity-50 blur-2xl"
        />
      </div>
      <div className="max-w-2xl mx-auto relative">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-100 mb-4 mt-10">Volunteer for The Healing Party</h1>
          {/* <p className="text-lg text-gray-600">Help us make a difference in our community</p> */}
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* email_address Field */}
            <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={formData.email_address}
                  onChange={(e) => handleInputChange("email_address", e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${errors.email_address ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-orange-500"
                    }`}
                  placeholder="your.email@example.com"
                />
              </div>
              {errors.email_address && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center mt-2 text-red-600 text-sm"
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.email_address}
                </motion.div>
              )}
            </motion.div>

            {/* phone_number Field */}
            <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 }}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => handleInputChange("phone_number", e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${errors.phone_number ? "border-red-300 focus:border-red-500" : "border-gray-200 focus:border-orange-500"
                    }`}
                  placeholder="+1 234 567 8900"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Include country code (e.g., +1, +44, +91)</p>
              {errors.phone_number && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center mt-2 text-red-600 text-sm"
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.phone_number}
                </motion.div>
              )}
            </motion.div>

            {/* Role Selection */}
            <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
              <label className="block text-sm font-semibold text-gray-700 mb-4">
                <Users className="inline w-5 h-5 mr-2" />
                Select Your Volunteer Role
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {roles.map((role, index) => (
                  <motion.div
                    key={role.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className={`relative cursor-pointer transition-all duration-200 ${formData.volunteer === role.id ? "transform scale-105" : "hover:scale-102"
                      }`}
                    onClick={() => handleInputChange("volunteer", role.id)}
                  >
                    <input
                      type="radio"
                      id={role.id}
                      name="role"
                      value={role.id}
                      checked={formData.volunteer === role.id}
                      onChange={() => handleInputChange("volunteer", role.id)}
                      className="sr-only"
                    />
                    <div
                      className={`p-4 border-2 rounded-lg transition-colors ${formData.volunteer === role.id
                        ? "border-orange-500 bg-orange-50"
                        : "border-gray-200 hover:border-gray-300"
                        }`}
                    >
                      <h3 className="font-semibold text-gray-800 mb-1">{role.label}</h3>
                      <p className="text-sm text-gray-600">{role.description}</p>
                      {formData.volunteer === role.id && (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-2 right-2">
                          <CheckCircle className="w-5 h-5 text-orange-500" />
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              {errors.volunteer && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center mt-2 text-red-600 text-sm"
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.volunteer}
                </motion.div>
              )}
            </motion.div>

            {/* Submit Button */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.8 }}>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700 hover:shadow-lg transform hover:scale-105"
                  }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Send className="w-5 h-5 mr-2" />
                    Submit Application
                  </div>
                )}
              </button>
            </motion.div>
          </form>
        </motion.div>
      </div>
      {showRegisterPrompt && ( 
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md">
          <div className="h-[100%] w-[100%] absolute" onClick={() => setShowRegisterPrompt(false)}></div>
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full text-center relative">
            <h3 className="text-lg font-bold text-gray-800 mb-3">User Not Found</h3>
            <p className="text-gray-600 mb-4">
              It looks like you haven't registered yet. Please register before applying as a volunteer.
            </p>
            <Link
              to="/healing-party"
              // target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition"
            >
              Go to Registration Page
            </Link>
            <button
              onClick={() => setShowRegisterPrompt(false)}
              className="block mt-4 text-sm text-gray-100 underline absolute -top-10 -right-8"
            >
              <X />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
